/**
 * OAuth token utilities for localStorage management
 */

import type { OAuthCredentials } from "./types"
import { OAUTH_TOKEN_KEYS } from "./types"

/**
 * Check if OAuth tokens exist in localStorage
 * Both access_token and refresh_token must be present and non-empty
 * @returns true if both tokens are present, false otherwise
 */
export function hasOAuthTokens(): boolean {
	if (typeof window === "undefined") {
		return false
	}

	try {
		const accessToken = localStorage.getItem(OAUTH_TOKEN_KEYS.ACCESS_TOKEN)
		const refreshToken = localStorage.getItem(OAUTH_TOKEN_KEYS.REFRESH_TOKEN)
		// Both must be present and non-empty (handles edge case: only one present)
		return (
			accessToken !== null &&
			refreshToken !== null &&
			accessToken !== "" &&
			refreshToken !== ""
		)
	} catch {
		// Handle localStorage unavailability (private browsing mode)
		return false
	}
}

/**
 * Get OAuth tokens from localStorage
 * @returns OAuthCredentials object or null if not found
 */
export function getOAuthTokens(): OAuthCredentials | null {
	if (typeof window === "undefined") {
		return null
	}

	try {
		const accessToken = localStorage.getItem(OAUTH_TOKEN_KEYS.ACCESS_TOKEN)
		const refreshToken = localStorage.getItem(OAUTH_TOKEN_KEYS.REFRESH_TOKEN)

		if (accessToken && refreshToken) {
			return { token: accessToken, refreshToken: refreshToken }
		}
		return null
	} catch {
		return null
	}
}

/**
 * Save OAuth tokens to localStorage
 * @param credentials OAuth tokens to save
 */
export function saveOAuthTokens(credentials: OAuthCredentials): void {
	if (typeof window === "undefined") {
		return
	}

	try {
		localStorage.setItem(OAUTH_TOKEN_KEYS.ACCESS_TOKEN, credentials.token)
		localStorage.setItem(
			OAUTH_TOKEN_KEYS.REFRESH_TOKEN,
			credentials.refreshToken,
		)
	} catch (error) {
		// Handle localStorage unavailability (private browsing mode, quota exceeded)
		console.warn("Failed to save OAuth tokens to localStorage:", error)
	}
}

/**
 * Clear OAuth tokens from localStorage
 */
export function clearOAuthTokens(): void {
	if (typeof window === "undefined") {
		return
	}

	try {
		localStorage.removeItem(OAUTH_TOKEN_KEYS.ACCESS_TOKEN)
		localStorage.removeItem(OAUTH_TOKEN_KEYS.REFRESH_TOKEN)
	} catch (error) {
		// Handle localStorage unavailability
		console.warn("Failed to clear OAuth tokens from localStorage:", error)
	}
}

/**
 * Extract tokens from API response body
 * @param responseBody Response from /exchange or /refresh endpoint
 * @returns OAuthCredentials or null if extraction failed
 */
export function extractTokensFromResponse(
	responseBody: unknown,
): OAuthCredentials | null {
	if (!responseBody || typeof responseBody !== "object") {
		return null
	}

	const body = responseBody as Record<string, unknown>

	// Handle both naming conventions (access_token/refresh_token vs token/refreshToken)
	const accessToken = body.access_token || body.token
	const refreshToken = body.refresh_token || body.refreshToken

	if (
		typeof accessToken === "string" &&
		typeof refreshToken === "string" &&
		accessToken &&
		refreshToken
	) {
		return {
			token: accessToken,
			refreshToken: refreshToken,
		}
	}

	return null
}
